import type {
  BloomSpaceGridSpan,
  BloomUniversalSpaceContent,
} from '../types/bloomUniversalContent';

/** 12 columns so quarter (3), third (4), half (6), wide (8), and full (12) share a row. */
const COLUMN_COUNT = 12;

/**
 * Contentful `gridSpan` → columns × rows on the large home mosaic.
 * `tall` is a quarter-width card that occupies two rows.
 */
const SPAN_CELLS: Record<
  BloomSpaceGridSpan,
  { readonly cols: number; readonly rows: number }
> = {
  full: { cols: 12, rows: 1 },
  wide: { cols: 8, rows: 1 },
  half: { cols: 6, rows: 1 },
  third: { cols: 4, rows: 1 },
  quarter: { cols: 3, rows: 1 },
  tall: { cols: 3, rows: 2 },
};

export interface PlacedBloomSpace {
  readonly space: BloomUniversalSpaceContent;
  readonly col: number;
  readonly row: number;
  readonly cols: number;
  readonly rows: number;
}

export function spanCells(span: BloomSpaceGridSpan): {
  readonly cols: number;
  readonly rows: number;
} {
  return SPAN_CELLS[span];
}

function cellKey(col: number, row: number): string {
  return `${col}:${row}`;
}

function slotFits(
  occupied: ReadonlySet<string>,
  col: number,
  row: number,
  cols: number,
  rows: number,
): boolean {
  if (col < 0 || col + cols > COLUMN_COUNT) {
    return false;
  }
  for (let r = row; r < row + rows; r += 1) {
    for (let c = col; c < col + cols; c += 1) {
      if (occupied.has(cellKey(c, r))) {
        return false;
      }
    }
  }
  return true;
}

function findSlot(
  occupied: ReadonlySet<string>,
  cols: number,
  rows: number,
  maxRow: number,
): { col: number; row: number } {
  const lastRow = maxRow + rows;
  for (let row = 0; row <= lastRow; row += 1) {
    for (let col = 0; col <= COLUMN_COUNT - cols; col += 1) {
      if (slotFits(occupied, col, row, cols, rows)) {
        return { col, row };
      }
    }
  }
  return { col: 0, row: lastRow };
}

/**
 * Packs spaces in `sortOrder` (already sorted) into the 12-column mosaic.
 * A `tall` card placed first sits on the left and the following cards fill beside it.
 */
export function placeBloomSpaceGrid(
  spaces: readonly BloomUniversalSpaceContent[],
): readonly PlacedBloomSpace[] {
  const occupied = new Set<string>();
  let maxRow = 0;
  const placed: PlacedBloomSpace[] = [];

  for (const space of spaces) {
    const { cols, rows } = spanCells(space.gridSpan);
    const slot = findSlot(occupied, cols, rows, maxRow);
    for (let r = slot.row; r < slot.row + rows; r += 1) {
      for (let c = slot.col; c < slot.col + cols; c += 1) {
        occupied.add(cellKey(c, r));
      }
    }
    maxRow = Math.max(maxRow, slot.row + rows - 1);
    placed.push({
      space,
      col: slot.col,
      row: slot.row,
      cols,
      rows,
    });
  }

  return placed;
}

export function bloomSpaceGridHeight(
  placed: readonly PlacedBloomSpace[],
  rowHeight: number,
  gap: number,
): number {
  const rowCount = placed.reduce(
    (max, item) => Math.max(max, item.row + item.rows),
    0,
  );
  if (rowCount === 0) {
    return 0;
  }
  return rowCount * rowHeight + (rowCount - 1) * gap;
}
